/**
 * PartnersPage - liste des partenaires integrateurs Office 365.
 *
 * v2026.06 : source des donnees passee de mock hardcode (STUB_PARTNERS) a
 * Cosmos partners via /api/partners (endpoint anonymous). Les partenaires sont
 * gerees dans l admin (app-admin) et seuls les actifs (active=true) sont
 * exposes ici. Fallback gracieux sur le mock si Cosmos est vide / API down
 * (en dev local sans backend par ex.).
 */
import {
  Card,
  Title3,
  Title2,
  Body1,
  Caption1,
  Button,
  Spinner,
  tokens,
  Badge,
} from '@fluentui/react-components';
import {
  PeopleTeam24Regular,
  Mail20Regular,
  Globe20Regular,
  Sparkle24Filled,
  Person20Regular,
  Location20Regular,
  Star20Filled,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../components/PageHeader';
import { partnersApi, type PublicPartner } from '../api/partnersApi';
import { onboardingApi } from '../api/onboardingApi';

interface PartnerStub {
  name:         string;
  tagline:      string;
  /** URL d un logo si dispo, sinon on rend l initiale dans un cercle colore. */
  logoUrl?:     string | null;
  /** Couleur de fallback du placeholder logo (hex). */
  logoColor:    string;
  /** Stack technique - tags affiches sous le logo. */
  technologies: string[];
  /** Personne de contact chez le partenaire. */
  contactName:  string;
  /** Email de contact (publication explicite OK). */
  email:        string;
  city:         string;
  country:      string;
  /** v2026.06 - Partenaire mis en avant (bandeau "Recommande"). */
  preferred?:   boolean;
  /** slug du partenaire (pour "Choisir ce partenaire" en auto-signup). */
  slug?:        string;
  /** v2026.06 - Description riche (HTML) saisie en admin / portail partenaire. */
  description?: string;
  /** v2026.06 - Page LinkedIn de la societe. */
  linkedinUrl?: string | null;
  /** v2026.06 - URL d'embed d'un post LinkedIn specifique. */
  linkedinEmbedUrl?: string | null;
}

// Whitelist stricte pour la description partenaire (HTML saisi par un tiers).
// Sanitizer maison (sans dépendance externe) : allowlist de balises, retrait de
// tout attribut sauf href/target/rel sûrs sur <a>, désencapsulation des balises
// non autorisées (on garde leur texte), et liens limités à http(s)/mailto/relatif.
const PARTNER_ALLOWED_TAGS = new Set([
  'P', 'BR', 'STRONG', 'B', 'EM', 'I', 'U', 'UL', 'OL', 'LI',
  'A', 'H3', 'H4', 'H5', 'SPAN', 'BLOCKQUOTE', 'HR',
]);

function sanitizeInto(src: Node, dest: Node, doc: Document): void {
  src.childNodes.forEach((child) => {
    if (child.nodeType === Node.TEXT_NODE) {
      dest.appendChild(doc.createTextNode(child.textContent ?? ''));
      return;
    }
    if (child.nodeType !== Node.ELEMENT_NODE) return; // ignore comments, etc.
    const el = child as Element;
    const tag = el.tagName.toUpperCase();
    if (!PARTNER_ALLOWED_TAGS.has(tag)) {
      // Balise non autorisée : on désencapsule (on garde le contenu nettoyé).
      sanitizeInto(el, dest, doc);
      return;
    }
    const clean = doc.createElement(tag);
    if (tag === 'A') {
      const href = (el.getAttribute('href') ?? '').trim();
      if (/^(https?:|mailto:|\/)/i.test(href)) {
        clean.setAttribute('href', href);
        clean.setAttribute('target', '_blank');
        clean.setAttribute('rel', 'noopener noreferrer nofollow');
      }
    }
    sanitizeInto(el, clean, doc);
    dest.appendChild(clean);
  });
}

function sanitizePartnerHtml(html: string): string {
  if (!html) return '';
  if (typeof DOMParser === 'undefined') return ''; // garde SSR (l'app est CSR)
  const parsed = new DOMParser().parseFromString(`<body>${html}</body>`, 'text/html');
  const out = parsed.createElement('div');
  sanitizeInto(parsed.body, out, parsed);
  return out.innerHTML;
}

// Valide qu'une URL d'embed pointe bien vers le domaine officiel LinkedIn embed.
function isValidLinkedInEmbed(url?: string | null): url is string {
  return !!url && /^https:\/\/www\.linkedin\.com\/embed\//.test(url);
}

// Palette de couleurs pour generer un hero band coherent quand le partner n a
// pas de logo. Hash du nom -> index pour stabilite (meme partner = meme couleur).
const FALLBACK_COLORS = ['#1F3D5C', '#7A1F23', '#1F6E70', '#2E5570', '#A86317', '#1F7A45'];
function pickColor(name: string): string {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return FALLBACK_COLORS[Math.abs(h) % FALLBACK_COLORS.length];
}

// Adapter : Cosmos PublicPartner -> shape PartnerStub utilisee par PartnerCard.
function adapt(p: PublicPartner): PartnerStub {
  return {
    name:         p.name ?? '(sans nom)',
    tagline:      p.tagline ?? '',
    logoUrl:      p.logoUrl ?? undefined,
    logoColor:    pickColor(p.name ?? ''),
    technologies: Array.isArray(p.technologies) ? p.technologies : [],
    contactName:  p.contactName ?? '',
    email:        p.email ?? '',
    city:         p.city ?? '',
    country:      p.country ?? '',
    preferred:    p.preferred ?? false,
    slug:         p.slug,
    description:  p.description ?? '',
    linkedinUrl:  p.linkedinUrl ?? null,
    linkedinEmbedUrl: p.linkedinEmbedUrl ?? null,
  };
}

// Liste pre-publiee a titre d apercu - les vrais partenaires seront listes
// depuis le Cosmos plateforme via une API publique au lancement.
// v2026.06 : etendue a 14 partenaires fictifs + flag `preferred` sur 3 d entre eux.
const STUB_PARTNERS: PartnerStub[] = [
  {
    name:         'Hybeos',
    tagline:      'Conseil cybersécurité + intégration M365',
    logoColor:    '#1F3D5C',
    technologies: ['Defender XDR', 'Entra ID P2', 'Purview DLP', 'Sentinel'],
    contactName:  'Nathanaël Stassart',
    email:        'contact@hybeos.eu',
    city:         'Bruxelles',
    country:      'BE',
    preferred:    true,
  },
  {
    name:         'CyberOne Solutions',
    tagline:      'MSSP / SOC managed pour M365 et Azure',
    logoColor:    '#7A1F23',
    technologies: ['Sentinel', 'Defender XDR', 'Lighthouse', 'Azure Arc'],
    contactName:  'Marie Lambert',
    email:        'partners@cyberone.be',
    city:         'Liège',
    country:      'BE',
    preferred:    true,
  },
  {
    name:         'NordSec Consulting',
    tagline:      'Audit NIS2 + ISO 27001 pré-certification',
    logoColor:    '#1F6E70',
    technologies: ['NIS2', 'ISO 27001', 'CyFun', 'Purview Compliance'],
    contactName:  'Pieter Janssens',
    email:        'audit@nordsec.be',
    city:         'Anvers',
    country:      'BE',
    preferred:    true,
  },
  {
    name:         'M365 Studio',
    tagline:      'Spécialistes Conditional Access + Purview DLP',
    logoColor:    '#2E5570',
    technologies: ['Conditional Access', 'Purview DLP', 'Intune', 'Defender for O365'],
    contactName:  'Stefan Vermeulen',
    email:        'hello@m365studio.be',
    city:         'Gand',
    country:      'BE',
  },
  {
    name:         'Beltrust IT',
    tagline:      'MSP région Wallonie - PME 50-500 sièges',
    logoColor:    '#A86317',
    technologies: ['Intune', 'Entra ID P1', 'Defender Business', 'M365 Backup'],
    contactName:  'François Dupuis',
    email:        'compliance@beltrust.be',
    city:         'Namur',
    country:      'BE',
  },
  {
    name:         'SecuLuxe',
    tagline:      'Cybersécurité Grand-Ducale - NIS2 cross-border',
    logoColor:    '#1F7A45',
    technologies: ['NIS2', 'Defender XDR', 'Entra ID', 'Microsoft Priva'],
    contactName:  'Anne Reuters',
    email:        'contact@seculuxe.lu',
    city:         'Luxembourg',
    country:      'LU',
  },
  {
    name:         'NetSafe Hexagone',
    tagline:      'Conformité NIS2 + LPM + Doctrine ANSSI',
    logoColor:    '#7A2B1F',
    technologies: ['NIS2', 'ANSSI', 'Sentinel', 'Defender for Cloud'],
    contactName:  'Julien Martin',
    email:        'partenaires@netsafe.fr',
    city:         'Paris',
    country:      'FR',
  },
  {
    name:         'Defender Pro',
    tagline:      'Spécialistes E5 + Add-ons sécurité avancée',
    logoColor:    '#4A2670',
    technologies: ['E5 Security', 'Copilot for Security', 'Defender CSPM', 'Sentinel'],
    contactName:  'Sarah Lefèvre',
    email:        'pro@defenderpro.eu',
    city:         'Lille',
    country:      'FR',
  },
  {
    name:         'CloudGuard NL',
    tagline:      'Microsoft Solutions Partner - Security & Compliance',
    logoColor:    '#2A6E1F',
    technologies: ['Defender XDR', 'Intune', 'Purview', 'Azure Landing Zones'],
    contactName:  'Jeroen de Vries',
    email:        'partners@cloudguard.nl',
    city:         'Amsterdam',
    country:      'NL',
  },
  {
    name:         'Zero Trust Lab',
    tagline:      'Architecture Zero Trust + Identity Governance',
    logoColor:    '#5C1F70',
    technologies: ['Entra ID Governance', 'Conditional Access', 'Privileged Identity', 'Defender for Identity'],
    contactName:  'Karim Bensaid',
    email:        'hello@zerotrustlab.be',
    city:         'Charleroi',
    country:      'BE',
  },
  {
    name:         'Compliance First',
    tagline:      'CISO-as-a-Service - ISO 27001 + GDPR + NIS2',
    logoColor:    '#1F4E70',
    technologies: ['ISO 27001', 'GDPR', 'NIS2', 'Purview Compliance'],
    contactName:  'Eve Vandenberghe',
    email:        'contact@compliancefirst.be',
    city:         'Mons',
    country:      'BE',
  },
  {
    name:         'Northbridge Cyber',
    tagline:      'MSSP haut-de-gamme - SOC 24/7 multi-tenant',
    logoColor:    '#704A1F',
    technologies: ['Sentinel', 'Defender XDR', 'Threat Intel', 'SOAR'],
    contactName:  'Mikael Persson',
    email:        'msp@northbridge-cyber.eu',
    city:         'Roskilde',
    country:      'DK',
  },
  {
    name:         'Ardent Sécurité',
    tagline:      'Spécialiste OT/IT pour secteur industriel NIS2',
    logoColor:    '#702A1F',
    technologies: ['Defender for IoT', 'OT Security', 'NIS2 Industrial', 'Microsoft Sentinel'],
    contactName:  'Bernard Gaudin',
    email:        'contact@ardent-securite.fr',
    city:         'Lyon',
    country:      'FR',
  },
  {
    name:         'Atelier365',
    tagline:      'Petites structures - Onboarding M365 simplifié',
    logoColor:    '#1F705A',
    technologies: ['M365 Business Premium', 'Intune Mobile', 'Defender Business'],
    contactName:  'Laura Maréchal',
    email:        'hello@atelier365.eu',
    city:         'Tournai',
    country:      'BE',
  },
];

// ----------------------------------------------------------------------------
// PartnerCard - card grande format avec hero coloree, logo, technologies,
// email, contact. Cf. demande user "il faut faire des cadres plus grands
// pour les partenaires avec Nom, Logo, Technologies, E-mail, Contact".
// ----------------------------------------------------------------------------
interface PartnerCardProps {
  partner:        PartnerStub;
  previewSoonTag: string;
  t:              (key: string) => string;
  /** Quand true, on garde le style "preview" (opacity reduite + tag soon). */
  isPreview?:     boolean;
  /** Cas auto-signup : bouton "Choisir ce partenaire" (définitif). */
  onChoose?:      (slug: string) => void;
  choosing?:      boolean;
}
function PartnerCard({ partner: p, previewSoonTag, t, isPreview = false, onChoose, choosing = false }: PartnerCardProps) {
  const initial = p.name.charAt(0).toUpperCase();
  // Preferred peut etre cumulable avec preview (fallback STUB Hybeos est preferred + preview).
  const isPreferred = !!p.preferred;
  return (
    <Card style={{
      // v2026.06 : opacity 1 partout (pas de degris meme en preview)
      opacity: 1,
      position: 'relative',
      border: isPreferred
        ? `1px solid ${tokens.colorBrandStroke1}`
        : `1px solid ${tokens.colorNeutralStroke2}`,
      padding: 0,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      // Ombre legere pour les preferred (signal subtil sans crier)
      boxShadow: isPreferred ? tokens.shadow8 : undefined,
      background: tokens.colorNeutralBackground1,
    }}>
      {/* Bandeau "Recommandé" en haut - utilise les tokens brand du theme */}
      {isPreferred && (
        <div style={{
          background: tokens.colorBrandBackground,
          color: tokens.colorNeutralForegroundOnBrand,
          padding: '4px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 5,
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
        }}>
          <Star20Filled style={{ width: 12, height: 12 }} />
          {t('partners.preferredBadge')}
        </div>
      )}

      {/* En-tete : logo + nom + tagline + localisation, theme-based (pas de couleur custom) */}
      <div style={{
        padding: 14,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
      }}>
        {p.logoUrl ? (
          <img
            src={p.logoUrl}
            alt={p.name}
            style={{
              width: 44, height: 44, borderRadius: 6, objectFit: 'contain',
              flexShrink: 0, background: tokens.colorNeutralBackground2, padding: 4,
              border: `1px solid ${tokens.colorNeutralStroke2}`,
            }}
          />
        ) : (
          <div style={{
            width: 44, height: 44, borderRadius: 6, flexShrink: 0,
            background: tokens.colorNeutralBackground3,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18, fontWeight: 700,
            color: tokens.colorNeutralForeground2,
            border: `1px solid ${tokens.colorNeutralStroke2}`,
          }}>
            {initial}
          </div>
        )}
        <div style={{ minWidth: 0, flex: 1 }}>
          <Body1 style={{ fontWeight: 600, fontSize: 14, lineHeight: 1.25, display: 'block' }}>
            {p.name}
          </Body1>
          <Caption1 style={{
            color: tokens.colorNeutralForeground2,
            display: 'block', marginTop: 2,
            fontSize: 11.5, lineHeight: 1.35,
          }}>
            {p.tagline}
          </Caption1>
          <Caption1 style={{
            color: tokens.colorNeutralForeground3,
            display: 'flex', alignItems: 'center', gap: 3,
            marginTop: 3, fontSize: 10.5,
          }}>
            <Location20Regular style={{ width: 11, height: 11 }} />
            {p.city} · {p.country}
          </Caption1>
        </div>
      </div>

      {/* Corps compact : technos + contact */}
      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
        {p.technologies.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {p.technologies.slice(0, 4).map((tech) => (
              <span key={tech} style={{
                padding: '2px 7px',
                background: tokens.colorNeutralBackground3,
                color: tokens.colorNeutralForeground2,
                fontSize: 10.5, fontWeight: 500,
                borderRadius: 999,
                border: `1px solid ${tokens.colorNeutralStroke2}`,
              }}>{tech}</span>
            ))}
            {p.technologies.length > 4 && (
              <span style={{
                fontSize: 10.5, color: tokens.colorNeutralForeground3,
                alignSelf: 'center',
              }}>+{p.technologies.length - 4}</span>
            )}
          </div>
        )}

        {/* Contact + email - condensed */}
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 3,
          paddingTop: 8,
          borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        }}>
          {p.contactName && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Person20Regular style={{ width: 13, height: 13, color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
              <Caption1 style={{ fontSize: 11.5, color: tokens.colorNeutralForeground1 }}>
                {p.contactName}
              </Caption1>
            </div>
          )}
          {p.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail20Regular style={{ width: 13, height: 13, color: tokens.colorNeutralForeground3, flexShrink: 0 }} />
              <a
                href={`mailto:${p.email}`}
                style={{ color: tokens.colorBrandForeground1, textDecoration: 'none', fontSize: 11.5 }}
              >
                {p.email}
              </a>
            </div>
          )}
        </div>

        {/* Cas auto-signup : choisir ce partenaire (devient définitif) */}
        {onChoose && p.slug && (
          <Button appearance="primary" size="small" disabled={choosing} style={{ marginTop: 4 }} onClick={() => onChoose(p.slug!)}>
            {choosing ? '…' : t('partners.choosePartner')}
          </Button>
        )}
      </div>

      {/* Tag "preview / soon" - en haut droite, theme-based */}
      {isPreview && (
        <div style={{
          position: 'absolute', top: isPreferred ? 30 : 8, right: 8,
          padding: '2px 7px',
          background: tokens.colorNeutralBackground3,
          color: tokens.colorNeutralForeground3,
          fontSize: 9.5, fontWeight: 700,
          letterSpacing: '0.05em', textTransform: 'uppercase',
          borderRadius: 999,
          border: `1px solid ${tokens.colorNeutralStroke2}`,
        }}>
          {previewSoonTag}
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------------------
// SinglePartnerView - page "partenaire unique" affichée quand le client est
// rattaché à un partenaire précis (signup avec partenaire).
// ----------------------------------------------------------------------------
function SinglePartnerView({ partner: p }: { partner: PartnerStub }) {
  const { t } = useTranslation();
  const initial = p.name.charAt(0).toUpperCase();
  return (
    <div>
      <PageHeader
        title={t('partners.myPartnerTitle', 'Votre partenaire')}
        description={t('partners.myPartnerSubtitle', 'Le partenaire qui vous accompagne dans votre mise en conformité NIS2 / CyFun.')}
      />
      {/* v2026.06 - Colonne gauche (partenaire + à propos) + post LinkedIn (droite) */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isValidLinkedInEmbed(p.linkedinEmbedUrl) ? 'minmax(0, 1fr) 440px' : 'minmax(0, 1fr)',
        gap: 20,
        alignItems: 'start',
        maxWidth: isValidLinkedInEmbed(p.linkedinEmbedUrl) ? 1220 : 760,
      }}>
      {/* Colonne gauche : bloc partenaire puis « À propos » empilés */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, minWidth: 0 }}>
      <Card style={{ padding: 0, overflow: 'hidden', border: `1px solid ${tokens.colorBrandStroke1}`, boxShadow: tokens.shadow8 }}>
        <div style={{ background: tokens.colorBrandBackground, color: tokens.colorNeutralForegroundOnBrand, padding: '6px 16px', fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Star20Filled style={{ width: 13, height: 13 }} /> {t('partners.myPartnerBadge', 'Votre partenaire dédié')}
        </div>
        <div style={{ padding: 24, display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {p.logoUrl ? (
            <img src={p.logoUrl} alt={p.name} style={{ width: 72, height: 72, borderRadius: 10, objectFit: 'contain', background: tokens.colorNeutralBackground2, padding: 6, border: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0 }} />
          ) : (
            <div style={{ width: 72, height: 72, borderRadius: 10, background: tokens.colorNeutralBackground3, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 30, fontWeight: 700, color: tokens.colorNeutralForeground2, border: `1px solid ${tokens.colorNeutralStroke2}`, flexShrink: 0 }}>{initial}</div>
          )}
          <div style={{ flex: 1, minWidth: 260 }}>
            <Title2 style={{ margin: 0 }}>{p.name}</Title2>
            <Body1 style={{ display: 'block', marginTop: 4, color: tokens.colorNeutralForeground2 }}>{p.tagline}</Body1>
            <Caption1 style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 6, color: tokens.colorNeutralForeground3 }}>
              <Location20Regular style={{ width: 13, height: 13 }} /> {p.city} · {p.country}
            </Caption1>
            {p.technologies.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 12 }}>
                {p.technologies.map((tech) => (
                  <span key={tech} style={{ padding: '3px 9px', background: tokens.colorNeutralBackground3, color: tokens.colorNeutralForeground2, fontSize: 11.5, borderRadius: 999, border: `1px solid ${tokens.colorNeutralStroke2}` }}>{tech}</span>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {p.contactName && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Person20Regular style={{ width: 14, height: 14, color: tokens.colorNeutralForeground3 }} />
                  <Body1 style={{ fontSize: 13 }}>{p.contactName}</Body1>
                </div>
              )}
              {p.email && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Mail20Regular style={{ width: 14, height: 14, color: tokens.colorNeutralForeground3 }} />
                  <a href={`mailto:${p.email}`} style={{ color: tokens.colorBrandForeground1, textDecoration: 'none', fontSize: 13 }}>{p.email}</a>
                </div>
              )}
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {p.email && (
                <Button appearance="primary" icon={<Mail20Regular />}
                  onClick={() => window.open(`mailto:${p.email}?subject=${encodeURIComponent('Accompagnement NIS365')}`)}>
                  {t('partners.contactPartner', 'Contacter mon partenaire')}
                </Button>
              )}
              {p.linkedinUrl && (
                <Button appearance="secondary" icon={<Globe20Regular />}
                  onClick={() => window.open(p.linkedinUrl!, '_blank', 'noopener,noreferrer')}>
                  {t('partners.viewLinkedin', 'Page LinkedIn')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

        {/* « À propos » — juste sous le bloc partenaire (même colonne) */}
        {p.description && sanitizePartnerHtml(p.description).trim() && (
          <Card style={{ padding: 24 }}>
            <Title3 style={{ margin: 0, marginBottom: 12 }}>
              {t('partners.aboutPartner', 'À propos de {{name}}').replace('{{name}}', p.name)}
            </Title3>
            <div
              className="partner-description"
              style={{ color: tokens.colorNeutralForeground1, fontSize: 14, lineHeight: 1.6 }}
              dangerouslySetInnerHTML={{ __html: sanitizePartnerHtml(p.description) }}
            />
          </Card>
        )}
      </div>{/* fin colonne gauche */}

        {/* Post LinkedIn (droite) — embed officiel, affiché seulement si l'URL est valide */}
        {isValidLinkedInEmbed(p.linkedinEmbedUrl) && (
          <Card style={{ padding: 0, overflow: 'hidden', border: `1px solid ${tokens.colorNeutralStroke2}`, position: 'sticky', top: 16 }}>
            <div style={{ padding: '10px 14px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, fontSize: 12, fontWeight: 600, color: tokens.colorNeutralForeground2, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Globe20Regular style={{ width: 14, height: 14 }} /> {t('partners.linkedinLatest', 'Dernière publication')}
            </div>
            <iframe
              src={p.linkedinEmbedUrl!}
              style={{ width: '100%', height: 580, border: 'none', display: 'block' }}
              title="LinkedIn embed"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          </Card>
        )}
      </div>
    </div>
  );
}

export function PartnersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [choosing, setChoosing] = useState<string | null>(null);

  async function choose(slug: string) {
    setChoosing(slug);
    try {
      await partnersApi.choosePartner(slug);
      await queryClient.invalidateQueries({ queryKey: ['onboarding-status'] });
    } catch { /* l'utilisateur peut réessayer */ } finally { setChoosing(null); }
  }

  // Partenaire rattaché au client (signup avec partenaire) via l'onboarding status.
  const onboardingQ = useQuery({
    queryKey: ['onboarding-status'],
    queryFn:  () => onboardingApi.status(),
    staleTime: 60_000,
    retry:     false,
  });
  const partnerSlug = onboardingQ.data?.partnerSlug ?? '';
  const partnerId   = onboardingQ.data?.partnerId ?? '';
  const hasPartner  = Boolean(partnerSlug || partnerId);

  // Source de verite : /api/partners (Cosmos partners filtre sur active=true)
  const { data, isLoading } = useQuery({
    queryKey: ['public-partners'],
    queryFn:  () => partnersApi.list(),
    staleTime: 60_000,
    retry:    false,
  });

  const realPartners = data?.partners ?? [];
  const usingFallback = realPartners.length === 0;

  if (isLoading || onboardingQ.isLoading) {
    return <div style={{ padding: 40, textAlign: 'center' }}><Spinner label="Chargement des partenaires..." /></div>;
  }

  // CAS 1 — le client est rattaché à un partenaire précis : page partenaire unique.
  const myPartner = hasPartner && !usingFallback
    ? realPartners.find((p) => (partnerSlug && p.slug === partnerSlug) || (partnerId && p.id === partnerId))
    : undefined;
  if (myPartner) {
    return <SinglePartnerView partner={adapt(myPartner)} />;
  }

  // CAS 2 — auto-signup sans partenaire : liste des partenaires PRÉFÉRÉS (pour choisir).
  const rawPartners: PartnerStub[] = usingFallback ? STUB_PARTNERS : realPartners.map(adapt);
  const preferred = rawPartners.filter((p) => p.preferred);
  const partners: PartnerStub[] = (preferred.length > 0 ? preferred : rawPartners)
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div>
      <PageHeader
        title={t('partners.title')}
        description={t('partners.subtitlePreferred', 'Choisissez un partenaire recommandé pour vous accompagner dans votre mise en conformité NIS2 / CyFun.')}
      />

      <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: 20 }}>
        {partners.length} partenaire{partners.length > 1 ? 's' : ''} recommandé{partners.length > 1 ? 's' : ''}.
      </Caption1>

      {(
        <div style={{
          display: 'grid',
          // v2026.06 - Cards plus petites (260px min) pour plus de densite (3-4 par ligne sur ecran large)
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 14,
        }}>
          {partners.map((p, i) => (
            <PartnerCard
              key={i}
              partner={p}
              previewSoonTag={t('partners.previewSoonTag')}
              t={t}
              isPreview={usingFallback}
              onChoose={choose}
              choosing={choosing === p.slug}
            />
          ))}
        </div>
      )}

      {/* CTA contact integrateurs */}
      <Card style={{ marginTop: 32, padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{
            width: 64, height: 64, borderRadius: 12,
            background: `linear-gradient(135deg, ${tokens.colorBrandBackground}, #2E8B96)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <PeopleTeam24Regular style={{ color: '#fff' }} />
          </div>
          <div style={{ flex: 1, minWidth: 280 }}>
            <Title3 style={{ margin: 0 }}>
              {t('partners.contact.title')}
            </Title3>
            <Caption1 style={{ color: tokens.colorNeutralForeground3, display: 'block', marginTop: 6 }}>
              {t('partners.contact.body')}
            </Caption1>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
            <Button
              appearance="primary"
              icon={<Mail20Regular />}
              onClick={() => window.open('https://nis365.eu/contact?subject=Partenariat', '_blank')}
            >
              {t('partners.becomePartner')}
            </Button>
            <Button
              appearance="subtle"
              icon={<Globe20Regular />}
              onClick={() => window.open('https://nis365.eu/partenaires', '_blank')}
              size="small"
            >
              {t('partners.contact.learnMore')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
